import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface ChartSeries {
  name: string;
  data: (number | null)[];
}

interface BuildingOpening {
  name: string;
  firstSale: number;
  avgPrice: number | null;
}

interface Props {
  data: { categories: string[]; series: ChartSeries[] };
  title?: string;
  neighborhoodName?: string;
  propertyTypeLabel?: string;
  buildingCategoryLabel?: string;
  buildingOpenings?: BuildingOpening[];
}

const FONT = "'Montserrat Light', 'Montserrat', sans-serif";
const FONT_SIZE = '11pt';
const FONT_STYLE = { fontFamily: FONT, fontSize: FONT_SIZE, fontWeight: 'normal' as const };

const SERIES_COLORS = ['#738FAF', '#CBA968'];
const MAX_VISIBLE_LABELS = 15;

export const SalesPSFChart = React.forwardRef<HighchartsReact.RefObject, Props>(
  ({ data, title = 'Sales Price per Square Foot', neighborhoodName = '', propertyTypeLabel = '', buildingCategoryLabel = '', buildingOpenings }, ref) => {
    const subtitle = `${neighborhoodName} | Average and Median ${buildingCategoryLabel ? buildingCategoryLabel + ' ' : ''}${propertyTypeLabel} Price Per Square Foot`;

    const lastIndex = data.categories.length - 1;
    const labelStep = Math.max(1, Math.ceil(data.categories.length / MAX_VISIBLE_LABELS));
    const currencyFormatter: Highcharts.DataLabelsFormatterCallbackFunction = function () {
      if (this.y == null) return '';
      if (labelStep > 1 && this.index % labelStep !== 0 && this.index !== lastIndex) return '';
      return '$' + Highcharts.numberFormat(this.y, 0);
    };

    const plotLines: Highcharts.XAxisPlotLinesOptions[] = [];
    if (buildingOpenings?.length) {
      const byIndex = new Map<number, BuildingOpening[]>();
      for (const b of buildingOpenings) {
        const idx = data.categories.indexOf(b.firstSale.toString());
        if (idx >= 0) {
          if (!byIndex.has(idx)) byIndex.set(idx, []);
          byIndex.get(idx)!.push(b);
        }
      }
      const sorted = Array.from(byIndex.entries()).sort((a, b) => a[0] - b[0]);
      const ROW_OFFSETS = [30, -10];
      for (let i = 0; i < sorted.length; i++) {
        const [idx, buildings] = sorted[i];
        const names = buildings
          .sort((a, b) => (b.avgPrice ?? 0) - (a.avgPrice ?? 0))
          .slice(0, 2)
          .map(b => b.name);
        const verb = names.length === 1 ? 'Opens' : 'Open';
        const y = -(names.length + 1) * 18 - 4 + ROW_OFFSETS[i % ROW_OFFSETS.length];
        plotLines.push({
          value: idx,
          color: '#aaa',
          width: 1,
          dashStyle: 'Dash',
          label: {
            text: `${names.join('<br/>')}<br/>${verb}`,
            align: 'center',
            verticalAlign: 'top',
            rotation: 0,
            y,
            style: { fontSize: '9pt', color: '#555', textAlign: 'center' }
          }
        });
      }
    }

    const options: Highcharts.Options = {
      chart: {
        type: 'line',
        height: 560,
        marginTop: 160,
        style: { fontFamily: FONT, fontSize: FONT_SIZE }
      },
      title: {
        text: title,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '30pt', fontWeight: '500', color: '#000000' }
      },
      subtitle: {
        text: subtitle,
        align: 'left',
        style: { fontFamily: "'Montserrat', sans-serif", fontSize: '14pt', fontWeight: '500', color: '#66635F' }
      },
      xAxis: {
        categories: data.categories,
        title: { text: 'Year', style: FONT_STYLE },
        labels: { style: FONT_STYLE },
        plotLines
      },
      yAxis: {
        title: { text: undefined },
        labels: {
          style: FONT_STYLE,
          formatter: function () {
            return '$' + Highcharts.numberFormat(this.value as number, 0);
          }
        },
        min: 0
      },
      legend: {
        itemStyle: FONT_STYLE
      },
      tooltip: {
        style: FONT_STYLE,
        formatter: function () {
          return this.points!.map(p =>
            `<span style="color:${p.color}">${p.series.name}</span>: <b>$${Highcharts.numberFormat(p.y!, 0)}</b>`
          ).join('<br/>');
        },
        shared: true
      },
      plotOptions: {
        line: {
          marker: { enabled: true, radius: 4 },
          connectNulls: false,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            formatter: currencyFormatter,
            style: {
              fontFamily: FONT,
              fontSize: FONT_SIZE,
              fontWeight: 'normal',
              textOutline: 'none',
              color: '#000000'
            }
          }
        }
      },
      colors: SERIES_COLORS,
      series: data.series.map(s => ({
        type: 'line' as const,
        name: s.name,
        data: s.data
      })),
      credits: { enabled: false }
    };

    return <HighchartsReact ref={ref} highcharts={Highcharts} options={options} />;
  }
);
